/**
 * Per-day nutrient targets (4.34) — WEB COPY.
 *
 * Line-equivalent copy of services/kidney/dailyTargets.ts (mobile); only the
 * imports above the marker differ. `__tests__/dailyTargets.test.ts` (root
 * jest) asserts the bodies stay identical. Edit both together.
 */

import type { KidneyProfile, TrackedNutrientKey, DailyLimits } from '@/types'
import {
  resolveNutrientTargets,
  CORE_TARGET_KEYS,
  OPTIONAL_TARGET_KEYS,
} from '@/kidney/targetResolution'
import { calculateNutrientLimits } from '@/kidney/limits'
import { effectiveCalculatedLimits, proteinIsGoalProfile } from '@/kidney/scoring'
import { isDialysisCondition } from '../../../shared/dialysis'
import { getFluidLimitByStage, defaultShowOxalate } from '@/kidney/platform'

type LimitsView = DailyLimits

// IMPORTS-END (everything below this line is asserted identical across copies)

export type TargetSource = 'custom' | 'legacy-min-mode' | 'calculated' | 'default';

export interface DayTargetValue {
  min?: number;
  max?: number;
  /** Provenance, kept so a historical min-only target stays distinguishable from a ceiling. */
  source: TargetSource;
}

export interface DayTargetsBasis {
  weightKg?: number;
  proteinPerKg?: number;
  condition?: string;
  /** Set when the targets were reconstructed for a day that had no snapshot. */
  backfilled?: true;
}

/** The persisted shape at `dailySummaries/{date}.targets` and the in-memory contract. */
export interface DayTargets {
  v: 1;
  values: Partial<Record<TrackedNutrientKey, DayTargetValue>>;
  basis: DayTargetsBasis;
}

export interface TargetInput {
  kidneyProfile: KidneyProfile | null | undefined;
  /** The oxalate default for stone formers is premium-gated, like the card itself. */
  isPremium: boolean;
}

/** One weight-history entry, reduced to what the diary passes around. */
export interface WeightPoint {
  date: string;          // 'YYYY-MM-DD'
  weightKg: number;
  timestamp?: number;    // epoch ms, for same-date ordering
  updatedAt?: number;    // epoch ms, tie-break after timestamp
}

/** Structural view of a `dailySummaries/{date}` document, as far as targets are concerned. */
export interface DayTargetsDocLike {
  targets?: DayTargets | null;
}

const DEFAULT_WEIGHT_KG = 70;
const DEFAULT_OXALATE_LIMIT_MG = 50;
/** 4.35: dietary calcium floor seeded for stone-only profiles (CUA / EAU: 1000–1200 mg/day with meals). */
const DEFAULT_CALCIUM_MIN_MG = 1000;

const ALL_TARGET_KEYS: readonly TrackedNutrientKey[] = [...CORE_TARGET_KEYS, ...OPTIONAL_TARGET_KEYS];

/** The weight as recorded, or undefined when it is missing or not a plausible body weight. */
function usableWeight(weight: number | undefined): number | undefined {
  if (typeof weight !== 'number' || !Number.isFinite(weight) || weight <= 20 || weight > 300) {
    return undefined;
  }
  return weight;
}

function safeWeight(weight: number | undefined): number {
  return usableWeight(weight) ?? DEFAULT_WEIGHT_KG;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

function isPositive(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

/**
 * Live targets for the profile as it is now. `opts.weightKg` substitutes the
 * profile weight (backfill) and goes through the same validation.
 * Returns null only when there is no kidney profile at all.
 */
export function resolveDailyTargets(
  input: TargetInput,
  opts?: { weightKg?: number },
): DayTargets | null {
  const kp = input.kidneyProfile;
  if (!kp) return null;

  const condition = kp.condition || 'not_sure_prevention';
  // 4.46 F7a: the effective map (the stored one, or rebuilt for Starting soon / a
  // pre-dialysis map under an active dialysis type), else the calculator.
  const calc = effectiveCalculatedLimits(kp)
    ?? calculateNutrientLimits(condition, kp.comorbidities, kp.dialysisType);

  const weightKg = safeWeight(opts?.weightKg ?? kp.weight);
  const proteinPerKg = isPositive(calc.proteinPerKg) ? calc.proteinPerKg : undefined;

  const resolved = resolveNutrientTargets(kp, {
    sodium: calc.sodium,
    potassium: calc.potassium,
    phosphorus: calc.phosphorus,
    protein: proteinPerKg !== undefined ? round1(proteinPerKg * weightKg) : 0,
    fluid: getFluidLimitByStage(condition, kp.dialysisType),
  });

  const values: Partial<Record<TrackedNutrientKey, DayTargetValue>> = {};
  for (const key of ALL_TARGET_KEYS) {
    const t = resolved[key];
    if (!t || t.source === 'none') continue;
    const v: DayTargetValue = { source: t.source };
    if (t.min !== undefined) v.min = t.min;
    if (t.max !== undefined) v.max = t.max;
    values[key] = v;
  }

  // 4.47 A2: on dialysis protein is a GOAL. The calculated default (always a ceiling from
  // the resolver) becomes a floor with the same number, so every reader that follows
  // `isMinOnlyTarget` shows "to go" / "Goal met" instead of "over". A user's own protein
  // target (custom / legacy) is never reshaped; past days keep their snapshots (Rule 9).
  const protein = values.protein;
  if (protein?.source === 'calculated' && protein.max !== undefined && calculatedProteinIsGoal(kp)) {
    values.protein = { min: protein.max, source: 'calculated' };
  }

  // Stone formers get the oxalate card by default (premium-gated, matching
  // useEffectiveOptionalNutrients); seed a standard daily budget so the card
  // has a progress bar / over-limit state until the user sets their own.
  // Seed only when the field was never set — an explicit null means the user
  // chose "track without limit".
  if (
    !values.oxalate &&
    kp.optionalNutrients?.dailyOxalateLimit === undefined &&
    input.isPremium === true &&
    // Stone-ONLY profiles, as before 4.35. A CKD user who ticks "I also get kidney stones"
    // gets the oxalate TRACKER but no seeded ceiling: a default max would start colouring
    // their days (oxalate is a warn key) on an unvalidated estimate, and the app does not
    // pick stone numbers for CKD users (same call as the calcium goal).
    condition === 'kidney_stones' &&
    defaultShowOxalate(kp)
  ) {
    values.oxalate = { max: DEFAULT_OXALATE_LIMIT_MG, source: 'default' };
  }

  // 4.35: a stone-ONLY profile gets a dietary calcium goal — a FLOOR of 1000 mg with no
  // seeded maximum, so a default can never colour a day. The guidance text carries the
  // "1000–1200 mg" range. Never seeded for CKD (the right amount depends on stage, binders
  // and supplements), never over a user target, and an explicit null ("track without
  // limit") suppresses it.
  if (
    !values.calcium &&
    kp.optionalNutrients?.dailyCalciumLimit === undefined &&
    input.isPremium === true &&
    condition === 'kidney_stones'
  ) {
    values.calcium = { min: DEFAULT_CALCIUM_MIN_MG, source: 'default' };
  }

  const basis: DayTargetsBasis = { weightKg, condition };
  if (proteinPerKg !== undefined) basis.proteinPerKg = proteinPerKg;

  return { v: 1, values, basis };
}

/**
 * 4.47 A2 — is the CALCULATED protein default a goal (a floor) for this profile? A real
 * dialysis condition AND the scorer's predicate (`proteinIsGoalProfile`: Starting soon keeps
 * the ceiling). A stale `onDialysis: true` on Stage 5 or on no condition keeps its ceiling
 * here, although the scorer still scores it as a target (accepted divergence, plan r2 P2-2).
 * Daily Limits labels the row with the same rule.
 */
export function calculatedProteinIsGoal(kp: KidneyProfile | null | undefined): boolean {
  return !!kp && isDialysisCondition(kp.condition) && proteinIsGoalProfile(kp);
}

/**
 * The 4.31 rendering contract: a min-only nutrient's "limit" IS its floor
 * (percentage-vs-floor drives "to go" / "goal met"); a range keeps the max as
 * the limit and carries its floor separately (see toMinTargets). Core keys
 * read 0 when nothing resolves (downstream guards are already `> 0`).
 */
export function toLimits(t: DayTargets | null): LimitsView {
  const primary = (key: TrackedNutrientKey): number | undefined => {
    const v = t?.values[key];
    if (!v) return undefined;
    return v.max !== undefined ? v.max : v.min;
  };
  const limits: LimitsView = {
    sodiumMg: primary('sodium') ?? 0,
    potassiumMg: primary('potassium') ?? 0,
    phosphorusMg: primary('phosphorus') ?? 0,
    proteinG: primary('protein') ?? 0,
    fluidMl: primary('fluid') ?? 0,
  };
  const optional: Partial<LimitsView> = {
    calories: primary('calories'),
    carbsG: primary('carbs'),
    fatG: primary('fat'),
    magnesiumMg: primary('magnesium'),
    calciumMg: primary('calcium'),
    vitaminKMcg: primary('vitaminK'),
    ironMg: primary('iron'),
    oxalateMg: primary('oxalate'),
    sugarsG: primary('sugars'),
    fiberG: primary('fiber'),
    saturatedFatG: primary('saturatedFat'),
  };
  // Keep unset optionals absent (not `undefined`) so views and writes stay clean.
  for (const k of Object.keys(optional) as (keyof LimitsView)[]) {
    if (optional[k] === undefined) delete optional[k];
  }
  return { ...limits, ...optional };
}

/** Every floor, whether the nutrient is min-only or a range. */
export function toMinTargets(t: DayTargets | null): Partial<Record<TrackedNutrientKey, number>> {
  const out: Partial<Record<TrackedNutrientKey, number>> = {};
  if (!t) return out;
  for (const key of ALL_TARGET_KEYS) {
    const v = t.values[key];
    if (v && v.min !== undefined) out[key] = v.min;
  }
  return out;
}

/** True when the nutrient's only bound is a floor (a goal, never a warning). */
export function isMinOnlyTarget(t: DayTargets | null, key: TrackedNutrientKey): boolean {
  const v = t?.values[key];
  return !!v && v.min !== undefined && v.max === undefined;
}

/** Deep value equality on `values` (bounds and provenance). Basis is ignored. */
export function targetsEqual(a: DayTargets | null, b: DayTargets | null): boolean {
  if (!a || !b) return a === b;
  for (const key of ALL_TARGET_KEYS) {
    const x = a.values[key];
    const y = b.values[key];
    if (!x && !y) continue;
    if (!x || !y) return false;
    if (x.min !== y.min || x.max !== y.max || x.source !== y.source) return false;
  }
  return true;
}

/** A stable string for effect dependencies / dedupe. */
export function targetsHash(t: DayTargets | null): string {
  if (!t) return '';
  const parts: string[] = [];
  for (const key of ALL_TARGET_KEYS) {
    const v = t.values[key];
    if (!v) continue;
    parts.push(`${key}:${v.min ?? ''}-${v.max ?? ''}:${v.source}`);
  }
  return parts.join('|');
}

/**
 * Estimate for a past day that has no snapshot: the live resolver, flagged as
 * an estimate. Every input is the profile as it is NOW — weight included, like
 * the condition, the comorbidities and the custom limits — because nothing
 * records what the profile weight was on an earlier date (see the file header).
 */
export function backfillTargetsForDate(input: TargetInput): DayTargets | null {
  const t = resolveDailyTargets(input);
  if (!t) return null;
  // 4.39: an estimate never carries an iron target. A missing snapshot does not prove
  // the day predates 4.34 (W1 can fail offline, spend its retries or be cut off at
  // midnight), and a goal the user sets today is no evidence it applied on that day,
  // so iron stays display-only there instead of being invented from today's profile.
  const values = { ...t.values };
  delete values.iron;
  return { ...t, values, basis: { ...t.basis, backfilled: true } };
}

function isStoredTargets(v: unknown): v is DayTargets {
  return !!v && typeof v === 'object' && (v as DayTargets).v === 1 && typeof (v as DayTargets).values === 'object';
}

/**
 * The one correction applied to a stored day: an ESTIMATE whose protein ceiling
 * was calculated from a weight the profile never carried (4.34 and 4.35 read it
 * out of the weight log). Returns the corrected snapshot, or null when there is
 * nothing to correct — the normal case, and the answer for every day the user
 * lived through, since a snapshot without `basis.backfilled` is never touched.
 *
 * ONLY the weight-calculated protein ceiling (or, from 4.47, the dialysis protein goal —
 * whichever bound the snapshot carries; the shape never changes) moves, recomputed as the
 * snapshot's OWN multiplier (`basis.proteinPerKg`, the g/kg that applied on
 * that date — never today's, which a stage change or dialysis would have moved)
 * times the current profile weight. Both ends must be weight-calculated, and an
 * estimate that carries no multiplier, or a profile with no usable weight, is
 * left alone rather than corrected onto an invented number. Everything else in
 * the snapshot — a doctor's sodium maximum, a custom protein target, optional
 * nutrients, the provenance of each — is carried across untouched, because
 * nothing else in a snapshot is derived from body weight.
 *
 * It is a pure function of the snapshot and the profile, so a phone and the web
 * app always show the same number for the same day, written back or not.
 */
export function repairedTargetsForDate(
  doc: DayTargetsDocLike | null | undefined,
  input: TargetInput,
): DayTargets | null {
  const stored = doc?.targets;
  if (!isStoredTargets(stored)) return null;
  if (stored.basis?.backfilled !== true) return null;     // lived through, not estimated
  const stale = stored.values.protein;
  if (!stale || stale.source !== 'calculated') return null;
  // 4.47 A3: a calculated protein target is a ceiling (`{max}`: ≤ 4.46, Starting soon) or,
  // on dialysis from 4.47, a goal (`{min}`). Repair whichever bound the snapshot carries and
  // keep its shape — the day is judged the way it was stored, never flipped to today's.
  const bound: 'max' | 'min' | undefined =
    stale.max !== undefined ? 'max' : stale.min !== undefined ? 'min' : undefined;
  if (bound === undefined) return null;
  const perKg = stored.basis.proteinPerKg;
  if (!isPositive(perKg)) return null;                    // nothing to recompute with
  const weightKg = usableWeight(input.kidneyProfile?.weight);
  if (weightKg === undefined) return null;                // profile still loading, or no weight recorded
  const live = resolveDailyTargets(input);
  if (live?.values.protein?.source !== 'calculated') return null;   // the user's own target now — leave it
  const value = round1(perKg * weightKg);
  if (value === stale[bound]) return null;
  return {
    ...stored,
    values: { ...stored.values, protein: { ...stale, [bound]: value } },
    basis: { ...stored.basis, weightKg },
  };
}

/**
 * Which targets apply to `date`:
 * - today or a future day → live
 * - a past day with a snapshot → the snapshot (live is ignored); if it is an
 *   ESTIMATE, its weight-calculated protein ceiling is corrected first
 *   (`repairedTargetsForDate`)
 * - a past day without one → backfill
 */
export function effectiveTargetsForDate(
  date: string,
  doc: DayTargetsDocLike | null | undefined,
  input: TargetInput,
  today: string,
): DayTargets | null {
  if (date >= today) return resolveDailyTargets(input);
  if (doc && isStoredTargets(doc.targets)) {
    return repairedTargetsForDate(doc, input) ?? doc.targets;
  }
  return backfillTargetsForDate(input);
}

/** True when a stored day still needs W3 (a past day with no snapshot). */
export function needsBackfill(date: string, doc: DayTargetsDocLike | null | undefined, today: string): boolean {
  if (date >= today) return false;
  return !(doc && isStoredTargets(doc.targets));
}
